// What the Bills list says after a bulk refresh, and which bills it has to warn
// about before starting one.
//
// Kept out of the page because the wording carries real information: a refresh
// re-derives usage, and a changed usage figure changes the variance a client is
// shown. "Done" would hide that.

import { isShadowOverridden } from './shadowOverride';
import type { BvBill } from '../types/billTypes';

/** One bill's result, as billRefreshService reports it. */
export type RefreshOutcomeLike = {
  billId: string;
  ok: boolean;
  repriced: boolean;
  usageFilled: string[];
  skipped?: 'overridden' | 'sent';
  error?: string;
};

export type BulkRefreshResult = {
  bills: BvBill[];
  outcomes: RefreshOutcomeLike[];
  /** Ids the server would not act on — another org's, or gone. */
  notFound: string[];
  repriced: number;
  skippedOverridden: number;
  /** Left alone because their report had gone to the customer. */
  skippedSent?: number;
  failed: number;
};

/**
 * The selected bills whose expected invoice was corrected by hand.
 *
 * Refreshing replaces those corrections with freshly calculated values (job 52).
 * One bill asks before doing that; a selection of forty has to ask louder.
 */
export function overriddenBills(bills: BvBill[]): BvBill[] {
  return bills.filter((b) => isShadowOverridden(b.shadow_result));
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** One sentence per thing that actually happened. Nothing that did not. */
export function summarizeRefresh(outcomes: RefreshOutcomeLike[]): string {
  if (outcomes.length === 0) return 'Nothing to refresh.';

  const repriced = outcomes.filter((o) => o.repriced);
  const parts = [`${plural(repriced.length, 'bill')} refreshed.`];

  // Usage is an input to the expected invoice, so a filled column moves the
  // variance. Naming the columns is the difference between a number someone can
  // check and a number that silently changed.
  const withUsage = outcomes.filter((o) => o.usageFilled.length > 0);
  if (withUsage.length > 0) {
    const columns = [...new Set(withUsage.flatMap((o) => o.usageFilled))];
    parts.push(`Usage filled in on ${withUsage.length} (${columns.join(', ')}).`);
  }

  const overridden = outcomes.filter((o) => o.skipped === 'overridden').length;
  if (overridden > 0) {
    parts.push(
      overridden === 1
        ? '1 left alone — its expected invoice was edited by hand.'
        : `${overridden} left alone — their expected invoices were edited by hand.`,
    );
  }

  const sent = outcomes.filter((o) => o.skipped === 'sent').length;
  if (sent > 0) {
    parts.push(
      sent === 1
        ? '1 left alone — its report was already sent to the customer.'
        : `${sent} left alone — their reports were already sent to the customer.`,
    );
  }

  const failed = outcomes.filter((o) => !o.ok);
  if (failed.length > 0) {
    const reasons = [...new Set(failed.map((o) => o.error).filter(Boolean))];
    parts.push(`${failed.length} failed: ${reasons.join(', ')}.`);
  }

  return parts.join(' ');
}
